import { useState, useCallback } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {"offline" | "online" | "mixed"} BillingMode
 * @typedef {"year" | "month"} DurationUnit
 */

/**
 * @typedef {Object} CreateAgreementPayload
 * @property {number} institution_id
 * @property {BillingMode} billing_mode
 * @property {number} duration_value
 * @property {DurationUnit} duration_unit
 * @property {number} price_per_unit
 * @property {number} base_licenses
 * @property {"pending"} [status] // optional
 */

/**
 * @typedef {Object} CreateAgreementResponse
 * @property {boolean} success
 * @property {string} message
 * @property {Object} data
 */

/**
 * Admin - Create School Agreement
 * @returns {{
 *   createAgreement: (payload: CreateAgreementPayload) => Promise<CreateAgreementResponse>,
 *   loading: boolean,
 *   error: string | null,
 *   success: boolean
 * }}
 */
export const useCreateAgreement = () => {
  const { user, fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const createAgreement = useCallback(
    async (payload) => {
      if (!user?.token) {
        throw new Error("User not authenticated");
      }

      setLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await fetchWithAuth(
          `${apiUrl}/school/agreement/store`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        );

        const result = await response.json();


        if (!response.ok || !result.success) {
          let firstErrorMessage;
          if (result?.errors) {
            firstErrorMessage = Object.values(result?.errors)[0][0];
          }
          if (result.message && !firstErrorMessage) {
            firstErrorMessage = result.message;
          }
          if (!firstErrorMessage) {
            firstErrorMessage = ERROR_MESSAGES.INTERNAL_SERVER;
          }
          throw new Error(firstErrorMessage || "Failed to create agreement");
        }

        setSuccess(true);
        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, fetchWithAuth, user],
  );

  return {
    createAgreement,
    loading,
    error,
    success,
  };
};
